import { cloneDeep, groupBy, omit, orderBy, sortBy, uniq } from 'lodash';
import { ClientApprovalBucketV2 as ClientApprovalBucketType } from 'types';

import { __t, applyTranslations } from './language';

import type { ClientApprovalBucket,
  ClientApprovalBucketItem,
  ClientApprovalBucketItemRaw,
  DeliverableAttachment,
  DeliverableAttachmentApprovalStatus,
  SelectOption,
  StatusLabelType } from 'types';

applyTranslations(require('./i18n/content-approval-util.i18n.json'));

export const compareByIds = (a: {
  id: number
}, b: {
  id: number
}) => a.id === b.id;
export const ATTACHMENT = 'attachment';
export const UNSORTED_BUCKET_ID = -1;
export const ITEMS_PER_ROW = 5;
export const APPROVAL_STATUSES: { [status: string]: DeliverableAttachmentApprovalStatus } = {
  PENDING: 'pending',
  APPROVED: 'approved',
  DECLINED: 'declined',
  NOT_REQUIRED: 'not_required'
};

export const CONTENT_APPROVAL_STATUS_LABEL_MAP: { [x: string]: StatusLabelType } = {
  approved: 'success',
  declined: 'danger',
  pending: 'warning',
  not_required: 'neutral'
};

const CONTENT_APPROVAL_SORT_MAP = {
  'created_at.desc': 'bucket_created_at',
  'title.asc': 'bucket_title',
  'creator.asc': 'creator_name'
};

const CLIENT_APPROVAL_SORT_MAP = {
  'updated_at.desc': 'bucket_updated_at',
  'title.asc': 'bucket_title',
  'creator.asc': 'creator_name'
};

export const CONTENT_APPROVAL_ADD_NEW_LOOK_ID = -10000; // use id that won't naturally occur

export const CONTENT_APPROVAL_SORT_KEYS: Array<string> = Object.keys(CONTENT_APPROVAL_SORT_MAP);
export const CLIENT_APPROVAL_SORT_KEYS: Array<string> = Object.keys(CLIENT_APPROVAL_SORT_MAP);

export const CONTENT_APPROVAL_SORT_OPTIONS: Array<SelectOption> = (
  Object.keys(CONTENT_APPROVAL_SORT_MAP).map(key => ({
    label: __t(`content_approval_util.sort_options.${CONTENT_APPROVAL_SORT_MAP[key]}`),
    value: key
  }))
);

export const CLIENT_APPROVAL_SORT_OPTIONS: Array<SelectOption> = (
  Object.keys(CLIENT_APPROVAL_SORT_MAP).map(key => ({
    label: __t(`content_approval_util.sort_options.${CLIENT_APPROVAL_SORT_MAP[key]}`),
    value: key
  }))
);

const hasPost = ({ items }, attachment) => items.find(({ attachmentId }) => attachmentId === attachment.id);

export function getUnsortedAttachmentsBucket(
  allAttachments: Array<ClientApprovalBucketItemRaw>,
  buckets: Array<ClientApprovalBucket> = []
): ClientApprovalBucket {
  let unsortedAttachments = allAttachments;
  if (buckets.length) {
    unsortedAttachments = allAttachments.filter(attachment => !buckets.some((bucket) => hasPost(bucket, attachment)));
  }
  return {
    id: UNSORTED_BUCKET_ID,
    title: __t('content_approval_util.unsorted_buckets_title'),
    notes: {},
    items: unsortedAttachments.map(({ id, fileName, assetType, url, approvalStatus }) => ({
      id,
      attachmentId: id,
      fileName,
      approvalStatus,
      mediaType: assetType,
      url,
      isSelected: false
    }))
  };
}

// special sort handling b/c:
//   1) we need `desc` sort
//   2) there are negative `id` numbers to consider
const sortLooksById = (buckets: Array<ClientApprovalBucket>, sortKey: string): Array<ClientApprovalBucket> => {
  const positives = buckets.filter(bucket => bucket.id >= 0);
  const negatives = buckets.filter(bucket => bucket.id < 0);

  positives.sort((a, b) => b[sortKey] - a[sortKey]);
  negatives.sort((a, b) => a[sortKey] - b[sortKey]);

  return negatives.concat(positives);
};

export const sortLooks = (buckets: Array<ClientApprovalBucket>, currentSort: string): Array<ClientApprovalBucket> => {
  const bucketsCopy = cloneDeep(buckets);

  const sortMap = {
    'created_at.desc': 'id',
    'title.asc': 'title'
  };

  const sortKey = sortMap[currentSort];

  if (sortKey === 'id') {
    return sortLooksById(bucketsCopy, sortKey);
  }

  return sortBy(bucketsCopy, bucket => bucket[sortKey] && bucket[sortKey].toLowerCase());
};

export const groupListByLook = (list: Array<ClientApprovalBucketType>): Array<Array<ClientApprovalBucketType>> => {
  const looks = uniq(list.map(item => item.look));
  // save sorting order which come from BE
  // for every look store first index of it's appearance in the list
  const looksSortIndexes = looks.reduce((res, look) => ({ ...res, [look]: list.findIndex(item => item.look === look) }), {});
  const groupedList = Object.values(groupBy(list, 'look'));

  // apply saved earlier sorting
  return orderBy(groupedList, group => looksSortIndexes[group[0].look]);
};

export const addSelectedStatusToLookItems = (buckets: Array<ClientApprovalBucket>): Array<ClientApprovalBucket> => buckets.map(bucket => ({
  ...bucket,
  items: bucket.items.map(item => ({
    ...item,
    isSelected: true
  }))
}));

export const toggleSelectedItemStatus = (buckets: Array<ClientApprovalBucket>, selectedIndex: number): Array<ClientApprovalBucket> => buckets.map((bucket, bucketIndex) => {
  // bucketIndex === 0 refers to the unsorted bucket (e.g. items that don't belong to a look)
  if (bucketIndex === 0) {
    return {
      ...bucket,
      items: bucket.items.map((item, itemIndex) => ({
        ...item,
        isSelected: itemIndex === selectedIndex ? !item.isSelected : item.isSelected
      }))
    };
  }

  return bucket;
});

export const setSelectedStatusForUnsorted = (buckets: Array<ClientApprovalBucket>, status: boolean): Array<ClientApprovalBucket> => buckets.map((bucket, bucketIndex) => {
  // bucketIndex === 0 refers to the unsorted bucket (e.g. items that don't belong to a look)
  if (bucketIndex === 0) {
    return {
      ...bucket,
      items: bucket.items.map((item) => ({
        ...item,
        isSelected: status
      }))
    };
  }

  return bucket;
});

export const resetApprovalStatus = (item: ClientApprovalBucketItem) => ({ ...item, approvalStatus: APPROVAL_STATUSES.PENDING });

type ClientApprovalBucketNew = Omit<ClientApprovalBucket, 'id'>;

export function processData(buckets: Array<ClientApprovalBucket>): Array<ClientApprovalBucketNew> {
  return buckets.slice(1).map(bucket => {
    if (bucket.id < UNSORTED_BUCKET_ID) {
      return omit(bucket, ['id']);
    }
    return bucket;
  });
}

let dummyIdCounter = UNSORTED_BUCKET_ID - 1;

export function getNewEmptyBucket(title: string) {
  return {
    id: dummyIdCounter--,
    title,
    notes: {
      brandRepresentative: '',
      accountManager: ''
    },
    items: []
  };
}

export function canFeedbackBeReset(attachmentsWithFeedback: Array<DeliverableAttachment>,
  captionStatus?: DeliverableAttachmentApprovalStatus,
  isCaption?: boolean): boolean {
  if (isCaption
    && captionStatus
    && [APPROVAL_STATUSES.APPROVED, APPROVAL_STATUSES.DECLINED].includes(captionStatus)) {
    return true;
  }

  return !!attachmentsWithFeedback.length;
}
